import { useCallback, useEffect, useState } from "react";
import { Check, X, Lock, Mail, Star, LogOut } from "lucide-react";
import Seo from "@/components/Seo";
import { getCountry } from "@/data/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORE = "hm_admin_key";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const Login = ({ onOk }) => {
  const [key, setKey] = useState("");
  const [err, setErr] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const r = await fetch(`${API}/admin/verify`, { headers: { "X-Admin-Key": key } });
    if (r.ok) { sessionStorage.setItem(KEY_STORE, key); onOk(key); } else setErr(true);
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="max-w-sm mx-auto bg-white rounded-3xl border border-pine/10 shadow-sm p-8 text-center">
      <span className="inline-flex w-12 h-12 rounded-full bg-blush-deep text-petal items-center justify-center"><Lock size={18} /></span>
      <h1 className="mt-5 font-serif font-semibold text-ink text-2xl">Mom's moderation desk</h1>
      <p className="mt-2 text-sm text-sage">Enter the admin key to review submissions.</p>
      <input data-testid="admin-key-input" type="password" value={key} onChange={(e) => { setKey(e.target.value); setErr(false); }} className="mt-6 w-full bg-cream border border-pine/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-petal/40" placeholder="Admin key" />
      {err && <p data-testid="admin-login-error" className="mt-2 text-sm text-petal-dark">That key isn't right.</p>}
      <button data-testid="admin-login-btn" className="mt-5 w-full bg-petal text-white text-sm font-semibold py-3 rounded-full hover:bg-pine transition-colors">Unlock</button>
    </form>
  );
};

const ReviewRow = ({ r, onAct, showActions }) => (
  <article data-testid={`admin-review-${r.id}`} className="bg-white rounded-2xl border border-pine/10 p-5 lg:p-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-petal">{getCountry(r.country_slug)?.name || r.country_slug} · {r.city}</p>
        <h3 className="mt-1 font-serif font-semibold text-ink text-lg">{r.title}</h3>
        <p className="text-xs text-sage mt-1">{r.name} · {r.email} · {r.years_lived || "—"} · {fmt(r.created_at)}</p>
      </div>
      <span className="flex items-center gap-1 text-petal text-sm font-mono"><Star size={13} className="fill-petal" /> {r.rating}/5</span>
    </div>
    <p className="mt-3 text-sm text-ink/85 leading-relaxed whitespace-pre-line">{r.body}</p>
    {showActions && (
      <div className="mt-4 flex gap-3">
        <button data-testid={`admin-approve-${r.id}`} onClick={() => onAct(r.id, "approve")} className="inline-flex items-center gap-1.5 bg-pine text-cream text-xs font-semibold px-4 py-2 rounded-full hover:bg-pine-dark transition-colors"><Check size={13} /> Approve &amp; publish</button>
        <button data-testid={`admin-reject-${r.id}`} onClick={() => onAct(r.id, "reject")} className="inline-flex items-center gap-1.5 border border-petal/40 text-petal-dark text-xs font-semibold px-4 py-2 rounded-full hover:bg-blush-deep transition-colors"><X size={13} /> Reject</button>
      </div>
    )}
  </article>
);

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORE) || "");
  const [view, setView] = useState("pending");
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    if (!key) return;
    const url = view === "contact" ? `${API}/admin/contact` : `${API}/admin/reviews?status=${view}`;
    const r = await fetch(url, { headers: { "X-Admin-Key": key } });
    if (r.status === 401) { sessionStorage.removeItem(KEY_STORE); setKey(""); return; }
    setItems(await r.json());
  }, [key, view]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    await fetch(`${API}/admin/reviews/${id}/${action}`, { method: "POST", headers: { "X-Admin-Key": key } });
    setItems((list) => list.filter((i) => i.id !== id));
  };

  const tabs = [["pending", "Pending"], ["approved", "Published"], ["rejected", "Rejected"], ["contact", "Messages"]];

  return (
    <div data-testid="admin-page" className="pt-28 lg:pt-32 pb-24 bg-cream grain relative min-h-screen">
      <Seo title="Moderation Desk | Hijrah Mom" siteName="Hijrah Mom" />
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        {!key ? (
          <Login onOk={setKey} />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-petal">Moderation desk</p>
                <h1 className="mt-2 font-serif font-bold text-ink text-3xl lg:text-4xl">Review the sisterhood's words.</h1>
              </div>
              <button data-testid="admin-logout-btn" onClick={() => { sessionStorage.removeItem(KEY_STORE); setKey(""); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-sage hover:text-petal"><LogOut size={13} /> Lock</button>
            </div>
            <div className="mt-8 flex gap-2 flex-wrap" role="tablist">
              {tabs.map(([id, label]) => (
                <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setView(id)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${view === id ? "bg-pine text-cream" : "bg-white border border-pine/15 text-pine hover:bg-blush-deep"}`}>{label}</button>
              ))}
            </div>
            <div className="mt-8 space-y-4" data-testid={`admin-list-${view}`}>
              {items.length === 0 && <p data-testid="admin-empty" className="text-sm text-sage border border-dashed border-pine/25 rounded-2xl p-8 text-center">Nothing here right now.</p>}
              {view === "contact"
                ? items.map((m) => (
                    <article key={m.id} data-testid={`admin-message-${m.id}`} className="bg-white rounded-2xl border border-pine/10 p-5 lg:p-6">
                      <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-petal">{m.topic} · {fmt(m.created_at)}</p>
                      <h3 className="mt-1 font-serif font-semibold text-ink text-lg">{m.name} <span className="text-sage text-sm font-sans">· {m.location || "no location"}</span></h3>
                      <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 text-xs text-pine hover:text-petal mt-1"><Mail size={12} /> {m.email}</a>
                      <p className="mt-3 text-sm text-ink/85 leading-relaxed whitespace-pre-line">{m.message}</p>
                      {m.connect_ok && <p className="mt-3 text-xs font-semibold text-pine">✓ Happy to be connected with other sisters</p>}
                    </article>
                  ))
                : items.map((r) => <ReviewRow key={r.id} r={r} onAct={act} showActions={view === "pending"} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
